import { Button, Card, Pagination, Table, TableColumn } from '@/components/common';
import { PaginationMetadata, RfidTagListItem } from '@/models';
import React from 'react';
import { formatAssignedTo, formatDateTime, joinNamedRefs } from './formatTagFields';

export interface RfidTagsTableProps {
  tags: RfidTagListItem[];
  pagination: PaginationMetadata | null;
  currentPage: number;
  pageSize: number;
  pageSizeOptions: readonly number[];
  isLoading: boolean;
  hasActiveFilters: boolean;
  unlinkingCardId: string | null;
  togglingCardId: string | null;
  canToggleActive: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onUnlink: (tag: RfidTagListItem) => void;
  onLink: (tag: RfidTagListItem) => void;
  onToggleActive: (tag: RfidTagListItem) => void;
}

const TruncatedList: React.FC<{ value: string }> = ({ value }) => {
  if (value === '-') return <>-</>;
  return (
    <span
      title={value}
      className="d-inline-block text-truncate"
      style={{ maxWidth: '220px', verticalAlign: 'bottom' }}
    >
      {value}
    </span>
  );
};

export const RfidTagsTable: React.FC<RfidTagsTableProps> = ({
  tags,
  pagination,
  currentPage,
  pageSize,
  pageSizeOptions,
  isLoading,
  hasActiveFilters,
  unlinkingCardId,
  togglingCardId,
  canToggleActive,
  onPageChange,
  onPageSizeChange,
  onUnlink,
  onLink,
  onToggleActive,
}) => {
  const columns: TableColumn<RfidTagListItem>[] = [
    {
      key: 'serialNumber',
      header: 'Serial',
      render: (tag) => tag.serialNumber || '-',
    },
    {
      key: 'isActive',
      header: 'Status',
      width: '120px',
      render: (tag) => {
        if (!canToggleActive) {
          return (
            <span className={`badge ${tag.isActive ? 'bg-success' : 'bg-secondary'}`}>
              {tag.isActive ? 'Active' : 'Inactive'}
            </span>
          );
        }
        const inputId = `rfid-active-${tag.cardId}`;
        const busy = togglingCardId === tag.cardId;
        return (
          <div className="form-check form-switch mb-0 d-flex align-items-center gap-2">
            <input
              id={inputId}
              type="checkbox"
              role="switch"
              className="form-check-input"
              checked={tag.isActive}
              disabled={busy}
              onChange={() => onToggleActive(tag)}
            />
            <label htmlFor={inputId} className="form-check-label small mb-0">
              {tag.isActive ? 'Active' : 'Inactive'}
            </label>
            {busy && (
              <span
                className="spinner-border spinner-border-sm text-muted"
                role="status"
                aria-hidden="true"
              />
            )}
          </div>
        );
      },
    },
    {
      key: 'assignedTo',
      header: 'Assigned to',
      render: (tag) => {
        const { name, email } = formatAssignedTo(tag);
        return (
          <div className="d-flex flex-column">
            <span>{name}</span>
            {email && name !== email && (
              <small className="text-muted text-truncate" title={email}>
                {email}
              </small>
            )}
          </div>
        );
      },
    },
    {
      key: 'labs',
      header: 'Labs',
      render: (tag) => <TruncatedList value={joinNamedRefs(tag.scopes?.labs)} />,
    },
    {
      key: 'expirationDate',
      header: 'Expires',
      render: (tag) => formatDateTime(tag.expirationDate),
    },
    {
      key: 'assignmentExpires',
      header: 'Assignment expires',
      render: (tag) => formatDateTime(tag.assignment?.expiresAt ?? null),
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '140px',
      render: (tag) =>
        tag.assignment && tag.user ? (
          <Button
            variant="danger"
            size="sm"
            outline
            icon="fas fa-unlink"
            loading={unlinkingCardId === tag.cardId}
            onClick={() => onUnlink(tag)}
          >
            Unlink
          </Button>
        ) : (
          <Button
            variant="primary"
            size="sm"
            outline
            icon="fas fa-link"
            onClick={() => onLink(tag)}
          >
            Link
          </Button>
        ),
    },
  ];

  const emptyMessage = hasActiveFilters
    ? 'No RFID tags match the current filters.'
    : 'No RFID tags found.';

  const totalCount = pagination?.totalCount ?? 0;
  const totalPages = pagination?.totalPages ?? 0;
  const rangeStart = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(currentPage * pageSize, totalCount);

  return (
    <Card className="border-0 shadow-sm">
      <Table
        columns={columns}
        data={tags}
        keyExtractor={(tag) => tag.cardId}
        isLoading={isLoading}
        emptyMessage={emptyMessage}
      />
      <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <small className="text-muted">Rows per page:</small>
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          {pagination && (
            <small className="text-muted">
              {totalCount === 0
                ? '0 tags'
                : `Showing ${rangeStart}–${rangeEnd} of ${totalCount} tags`}
            </small>
          )}
        </div>
        {totalPages > 1 && <Pagination pagination={pagination} onPageChange={onPageChange} />}
      </div>
    </Card>
  );
};

export default RfidTagsTable;
